/**
 * Semantic comparison helpers for the recommendation "Porównanie zmian" table.
 *
 * The LLM snapshot and the target call describe the same facts in different
 * vocabularies, so plain `===` would flag almost every row as "changed":
 * - status: snapshot carries GrantStatus (OPENED, RESULTS, ...), the call carries CallStatus (OPEN, RESOLVED, ...)
 * - dates: snapshot is Warsaw wall time with a bogus trailing "Z", the call is a real UTC instant
 * - regions: diacritic / ASCII / capitalised variants of the same voivodeship
 * - allocation: numeric(15,2) is served as a string ("800000.00"), the snapshot carries a number
 *
 * Every helper is fail-open: anything it cannot interpret counts as a difference,
 * so the moderator is never told "unchanged" about a value we did not understand.
 *
 * @see docs/specifications/2026-10-21-recommendation-diff-emphasis/spec.md
 */
import { normalizeRegion } from "../../../constants/regions";

/**
 * Status vocabulary of the LLM snapshot (GrantStatus, ADR-024). A closed union so
 * that every map keyed by it (EXTRACTION_STATUS_TO_CALL_STATUS here,
 * EXTRACTION_STATUS_LABELS in FieldChangesTable.tsx) is checked for completeness
 * by the compiler.
 */
export type ExtractionStatus =
  | "OPENED"
  | "CLOSED"
  | "CANCELLED"
  | "SUSPENDED"
  | "RESUMED"
  | "EXTENDED"
  | "UNDER_REVIEW"
  | "RESULTS"
  | "UPDATE";

/**
 * Snapshot (LLM) status -> CallStatus the backend applies on approval.
 * `null` = informational signal, never a status change.
 *
 * Manual mirror of the backend mapper: the compiler only checks the KEYS, the
 * VALUES must be kept in sync by hand (the util carries a @see back to this file).
 * Not a 1:1 copy of the util: `UPDATE` maps to ANNOUNCED there, but approval
 * skips it (`snapshot.status !== 'UPDATE'`), hence `null` here.
 *
 * @see apps/backend/src/grants/calls/shared/status-mapper.util.ts (mapExtractedStatusToCallStatus)
 * @see apps/backend/src/grants/calls/services/call-recommendation.service.ts (UPDATE is skipped on apply)
 * @see docs/architecture/adr/024-extracted-data-snapshot-status-field.md
 */
export const EXTRACTION_STATUS_TO_CALL_STATUS: Record<ExtractionStatus, string | null> = {
  OPENED: "OPEN",
  RESUMED: "OPEN",
  EXTENDED: "OPEN",
  CLOSED: "CLOSED",
  CANCELLED: "CANCELLED",
  SUSPENDED: "SUSPENDED",
  UNDER_REVIEW: "UNDER_REVIEW",
  RESULTS: "RESOLVED",
  UPDATE: null,
};

export const hasOwn = (obj: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key);

/**
 * True when the LLM snapshot status leaves the call's status as it is.
 * Only the snapshot value is normalised; a status outside the snapshot
 * vocabulary (including CallStatus values such as "OPEN") counts as a change.
 */
export const statusValuesEqual = (callStatus: unknown, llmStatus: unknown): boolean => {
  if (typeof llmStatus !== "string" || !hasOwn(EXTRACTION_STATUS_TO_CALL_STATUS, llmStatus)) {
    return false;
  }
  const mapped = EXTRACTION_STATUS_TO_CALL_STATUS[llmStatus as ExtractionStatus];
  if (mapped === null) return true;
  return mapped === callStatus;
};

const MS_PER_MINUTE = 60 * 1000;
const MINUTES_PER_HOUR = 60;

const WARSAW_TIME_ZONE = "Europe/Warsaw";

/**
 * Created once at module load. An engine without `longOffset` support throws here;
 * `null` then makes every date comparison a difference (fail-open).
 */
const createWarsawOffsetFormatter = (): Intl.DateTimeFormat | null => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: WARSAW_TIME_ZONE,
      timeZoneName: "longOffset",
    });
  } catch {
    return null;
  }
};

const warsawOffsetFormatter = createWarsawOffsetFormatter();

/**
 * UTC offset of Europe/Warsaw (minutes, +60 CET / +120 CEST) at the given
 * moment. Taken from Intl, never hardcoded, so DST is handled.
 */
const getWarsawOffsetMinutes = (utcMs: number): number | null => {
  if (!warsawOffsetFormatter) return null;
  const tzPart = warsawOffsetFormatter.formatToParts(new Date(utcMs)).find((p) => p.type === "timeZoneName");
  const match = tzPart?.value.match(/GMT([+-])(\d{2}):(\d{2})/);
  if (!match) return null;
  const sign = match[1] === "+" ? 1 : -1;
  return sign * (parseInt(match[2], 10) * MINUTES_PER_HOUR + parseInt(match[3], 10));
};

const stripTimezone = (datetime: string): string => {
  if (datetime.endsWith("Z")) return datetime.slice(0, -1);
  if (/[+-]\d{2}:\d{2}$/.test(datetime)) return datetime.slice(0, -6);
  return datetime;
};

/**
 * Interprets an LLM datetime as Europe/Warsaw wall time (any trailing "Z" or
 * ±hh:mm is ignored, "YYYY-MM-DD" means midnight) and returns the instant in
 * epoch milliseconds, or null when the value cannot be interpreted.
 *
 * The offset is evaluated at the wall time read as UTC, exactly like the backend,
 * so "unchanged" here matches "no change applied" on approval. Consequence (kept on
 * purpose, parity with the backend): inside the one-hour DST transition window the
 * result is shifted by an hour. Do not fix it on one side only.
 *
 * @see apps/backend/src/grants/analysis/utils/convert-naive-datetime-to-warsaw.util.ts (convertNaiveDatetimeToWarsaw)
 */
export const warsawWallTimeToInstant = (value: unknown): number | null => {
  if (typeof value !== "string" || value === "") return null;
  const naive = stripTimezone(value);
  const utcMs = Date.parse(
    naive.includes("T") ? `${naive}+00:00` : `${naive}T00:00:00+00:00`
  );
  if (isNaN(utcMs)) return null;
  const offsetMinutes = getWarsawOffsetMinutes(utcMs);
  if (offsetMinutes === null) return null;
  return utcMs - offsetMinutes * MS_PER_MINUTE;
};

/**
 * Compares the call's real UTC instant with the LLM's Warsaw wall time as
 * instants (milliseconds). A difference in the hour alone IS a difference.
 *
 * Call side projection: the API serves `applicationStartDatetime ?? applicationStartDate`.
 * A legacy DATE-only value ("YYYY-MM-DD") parses as UTC midnight, not Warsaw
 * midnight, so it never equals the LLM value (fail-open: shown as a change).
 * @see apps/backend/src/grants/calls/controllers/recommendations.controller.ts (targetCall projection)
 */
export const dateValuesEqual = (callValue: unknown, llmValue: unknown): boolean => {
  if (typeof callValue !== "string") return false;
  const callMs = Date.parse(callValue);
  const llmMs = warsawWallTimeToInstant(llmValue);
  if (isNaN(callMs) || llmMs === null) return false;
  return callMs === llmMs;
};

const toRegionSet = (regions: unknown[]): string[] =>
  Array.from(new Set(regions.map((region) => normalizeRegion(String(region))))).sort();

/**
 * Compares two region lists as sets of canonical slugs: order, duplicates,
 * capitalisation and diacritics (dolnośląskie / dolnoslaskie) do not matter.
 *
 * @see apps/frontend/src/constants/regions.ts (normalizeRegion, mirrors the backend region.constants.ts used on approval)
 */
export const regionSetsEqual = (a: unknown, b: unknown): boolean => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  const setA = toRegionSet(a);
  const setB = toRegionSet(b);
  return setA.length === setB.length && setA.every((region, i) => region === setB[i]);
};

/**
 * A finite number from a number or a non-blank numeric string; null otherwise.
 * Shared by the allocation comparison and the allocation formatter.
 */
export const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  // Number("") and Number("  ") are 0, so blank strings must be rejected up front
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Numeric equality for values that may be a number or a numeric string
 * (`allocation` is numeric(15,2): the API serves "800000.00", the LLM a number).
 * Returns null when either side is not a finite number / non-blank parseable string
 * (null, undefined, "", NaN, "abc"): the comparison is undecided and the caller
 * falls back to a literal comparison (D-K), so `"abc"` vs `"abc"` is unchanged.
 *
 * @see apps/backend/src/grants/calls/services/call-recommendation.service.ts (allocation: Number(a) !== Number(b))
 */
export const numericValuesEqual = (a: unknown, b: unknown): boolean | null => {
  const numA = toFiniteNumber(a);
  const numB = toFiniteNumber(b);
  if (numA === null || numB === null) return null;
  return numA === numB;
};

/** Date and time of an instant as shown in the table, always in Europe/Warsaw. */
export interface WarsawDisplayParts {
  /** DD.MM.YYYY */
  date: string;
  /** HH:mm (24 h) */
  time: string;
}

const pad2 = (value: number): string => String(value).padStart(2, "0");

const createWarsawDisplayFormatter = (): Intl.DateTimeFormat | null => {
  try {
    return new Intl.DateTimeFormat("pl-PL", {
      timeZone: WARSAW_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  } catch {
    return null;
  }
};

const warsawDisplayFormatter = createWarsawDisplayFormatter();

/**
 * Display parts of a real UTC instant (the target call side) in Europe/Warsaw,
 * independent of the browser time zone. Null when the value is not a date.
 */
export const callInstantToWarsawParts = (value: unknown): WarsawDisplayParts | null => {
  if (typeof value !== "string" || value === "") return null;
  const ms = Date.parse(value);
  if (isNaN(ms)) return null;
  const date = new Date(ms);
  if (!warsawDisplayFormatter) {
    // Engine without time zone support: best effort in the local zone
    return {
      date: `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`,
      time: `${pad2(date.getHours())}:${pad2(date.getMinutes())}`,
    };
  }
  const parts = warsawDisplayFormatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  return { date: `${get("day")}.${get("month")}.${get("year")}`, time: `${get("hour")}:${get("minute")}` };
};

/**
 * Display parts of an LLM value, which already IS Warsaw wall time (any trailing
 * "Z" / ±hh:mm is bogus): the digits are shown as they are, like the date input
 * in edit mode does. Null when the value is not a date.
 */
export const llmWallTimeToWarsawParts = (value: unknown): WarsawDisplayParts | null => {
  if (typeof value !== "string" || value === "") return null;
  const naive = stripTimezone(value);
  if (isNaN(Date.parse(naive.includes("T") ? `${naive}+00:00` : `${naive}T00:00:00+00:00`))) {
    return null;
  }
  const match = naive.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!match) return null;
  return { date: `${match[3]}.${match[2]}.${match[1]}`, time: `${match[4] ?? "00"}:${match[5] ?? "00"}` };
};
